import Phaser from "phaser";
import { SpriteLoader } from "../sprite-loader";
import { Item, ItemDefinition, ItemType, getItemStat } from "../models/common";
import { COLORS, hexToPhaser } from "./colors";
import { getAmmoColor } from "../utils/item-utils";

export class ItemUI {
  /**
   * Renders an item UI (sprite, name, price) at the given position in the scene.
   * Returns an array of Phaser GameObjects for container composition.
   */
  static render(
    scene: Phaser.Scene,
    item: ItemDefinition,
    x: number,
    y: number,
    size: number,
  ): Phaser.GameObjects.Sprite | null {
    // Sprite
    if (
      item.spriteSheet &&
      typeof item.spriteX === "number" &&
      typeof item.spriteY === "number"
    ) {
      // Import SpriteLoader directly in windows
      const sprite = SpriteLoader.createSprite(
        scene,
        item.spriteSheet,
        item.spriteX,
        item.spriteY,
        x + size / 2,
        y + size / 2,
        true,
      );
      sprite.displayWidth = size * 0.85;
      sprite.displayHeight = size * 0.85;
      return sprite;
    }
    return null;
  }

  /**
   * Renders a small gold "R" badge in the top-left corner of a slot for Recipe-type items.
   * Returns an array of GameObjects to push into the slot's sprite list, or null if not a recipe.
   * Call this after rendering the base sprite so the badge draws on top.
   */
  static renderRecipeBadge(
    scene: Phaser.Scene,
    item: ItemDefinition,
    slotX: number,
    slotY: number,
  ): Phaser.GameObjects.GameObject[] | null {
    if (item.type !== ItemType.Recipe) return null;

    const badgeSize = 14;
    const badgeX = slotX + badgeSize / 2;
    const badgeY = slotY + badgeSize / 2;

    const bg = scene.add.rectangle(badgeX, badgeY, badgeSize, badgeSize, 0xf0a500, 1);
    bg.setScrollFactor(0);

    const label = scene.add.text(badgeX, badgeY, "R", {
      fontSize: "9px",
      color: "#000000",
      fontFamily: "Anton",
      fontStyle: "bold",
    });
    label.setOrigin(0.5).setScrollFactor(0);

    return [bg, label];
  }

  /**
   * Renders a small count badge in the bottom-left corner of a slot when the stack count is > 1.
   * Returns an array of GameObjects to push into the slot's sprite list, or null if count ≤ 1.
   */
  static renderCountBadge(
    scene: Phaser.Scene,
    item: Item,
    slotX: number,
    slotY: number,
    slotSize: number,
  ): Phaser.GameObjects.GameObject[] | null {
    const count = item.count ?? 1;
    if (count <= 1) return null;

    const badgeSize = 20;
    const margin = 1;
    const badgeX = slotX + slotSize - badgeSize / 2 - margin;
    const badgeY = slotY + slotSize - badgeSize / 2 - margin;

    const bg = scene.add.rectangle(badgeX, badgeY, badgeSize, badgeSize, 0x000000, 0.7);
    bg.setScrollFactor(0);

    const label = scene.add.text(badgeX, badgeY, `${count}`, {
      fontSize: "18px",
      color: "#ffffff",
      fontFamily: "Anton",
      fontStyle: "bold",
    });
    label.setOrigin(0.5).setScrollFactor(0);

    return [bg, label];
  }

  /**
   * Renders a small ammo status indicator circle in the lower-right corner of an item slot.
   * Returns an Arc if the item is a weapon with ammo, or null for items without ammo (e.g. melee).
   * Color: green (≥50% ammo), orange (<50% ammo), red (0 ammo).
   * slotX/slotY should be in the container's local coordinate space (world coords for (0,0) containers).
   */
  static renderAmmoIndicator(
    scene: Phaser.Scene,
    item: Item,
    slotX: number,
    slotY: number,
    slotSize: number,
  ): Phaser.GameObjects.Arc | null {
    const maxAmmo = getItemStat(item, "ammo");
    if (maxAmmo <= 0) return null;

    const currentAmmo = item.currentAmmo ?? 0;
    const color = hexToPhaser(getAmmoColor(currentAmmo, maxAmmo));

    const radius = 6;
    const margin = 2;
    const circleX = slotX + slotSize - radius - margin;
    const circleY = slotY + slotSize - radius - margin;
    const circle = scene.add.arc(
      circleX,
      circleY,
      radius,
      0,
      360,
      false,
      color,
      1,
    );
    circle.setScrollFactor(0);
    return circle;
  }
}
